/**
 * /saas/cohorts — Cohort retention heat-grid.
 *
 * Two views (Logo = customer count, NDR = MRR) × optional segment axis
 * (Plan / Acquisition channel / Size band / Region). Renders as small-
 * multiples when segmented. Cells colored on a 5-stop scale; low-
 * confidence cohorts (n0 < min_cohort_size) get a dimmed background +
 * dotted border so the eye doesn't read noise as signal.
 *
 * API surface used:
 *   GET  /api/saas/metrics/cohort-retention?view=&segment=&min_cohort_size=
 *   POST /api/saas/metrics/cohort-retention/snapshot?target_month=YYYY-MM-DD
 *   POST /api/saas/metrics/cohort-retention/backfill
 *
 * Why NDR cells can exceed 100%: that's the whole point of NDR — expansions,
 * upsells, seat growth lifting a cohort's MRR above its starting base. We
 * color >100% the deepest green so it's celebrated, not flattened.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import HistoryIcon from "@mui/icons-material/History";

import { api, errorMessage } from "@/lib/api";

// ─── Types — mirror app/modules/saas/services/cohort_retention.py output ─

type CohortCellLogo = {
  month_index: number;
  value: number;          // percent (0-100+)
  active: number;
  n0: number;
  low_confidence: boolean;
};

type CohortCellNdr = {
  month_index: number;
  value: number;          // percent (0-100+; >100 = expansion)
  mrr_cents: number;
};

type Cohort = {
  cohort_month: string;   // ISO date (month-end)
  n0?: number;            // logo: customer count at month 0
  n0_mrr_cents?: number;  // ndr: cohort base MRR
  cells: (CohortCellLogo | CohortCellNdr)[];
};

type CohortGridBlended = {
  view: "logo" | "ndr";
  segment: null;
  cohorts: Cohort[];
};

type CohortGridSegmented = {
  view: "logo" | "ndr";
  segment: string;
  min_cohort_size: number;
  segments: { segment_value: string; cohorts: Cohort[] }[];
};

type CohortGrid = CohortGridBlended | CohortGridSegmented;

// ─── Route ────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/_auth/saas/cohorts")({
  component: CohortsPage,
});

const SEGMENT_OPTIONS = [
  { value: "", label: "Blended (no segment)" },
  { value: "plan_tier", label: "Plan tier" },
  { value: "acq_channel", label: "Acquisition channel" },
  { value: "size_band", label: "Size band (seats)" },
  { value: "region", label: "Region" },
] as const;

function CohortsPage() {
  const qc = useQueryClient();
  const [view, setView] = useState<"logo" | "ndr">("logo");
  const [segment, setSegment] = useState<string>("");
  const [minCohortSize, setMinCohortSize] = useState<number>(10);
  const [toast, setToast] = useState<{ msg: string; severity: "success" | "error" | "info" } | null>(null);

  const gridQuery = useQuery({
    queryKey: ["saas", "cohort-grid", view, segment, minCohortSize],
    queryFn: () => {
      const params = new URLSearchParams({
        view,
        min_cohort_size: String(minCohortSize),
      });
      if (segment) params.set("segment", segment);
      return api<CohortGrid>(`/api/saas/metrics/cohort-retention?${params.toString()}`);
    },
  });

  const snapshotNow = useMutation({
    mutationFn: () => {
      const today = new Date().toISOString().slice(0, 10);
      return api<{ snapshot_month: string; rows_written: number; active_customers: number; total_mrr_cents: number }>(
        `/api/saas/metrics/cohort-retention/snapshot?target_month=${today}`,
        { method: "POST" },
      );
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["saas", "cohort-grid"] });
      setToast({
        msg: `Snapshot ${r.snapshot_month} — ${r.active_customers} active, ${dollars(r.total_mrr_cents)} MRR`,
        severity: "success",
      });
    },
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });

  const backfill = useMutation({
    mutationFn: () =>
      api<{ months_processed: number; snapshots: unknown[] }>(
        "/api/saas/metrics/cohort-retention/backfill",
        { method: "POST" },
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["saas", "cohort-grid"] });
      setToast({
        msg: `Backfilled ${r.months_processed} month${r.months_processed === 1 ? "" : "s"}`,
        severity: "success",
      });
    },
    onError: (e) => setToast({ msg: errorMessage(e), severity: "error" }),
  });

  const isSegmented = gridQuery.data && "segments" in gridQuery.data;
  const cohortCount = useMemo(() => {
    if (!gridQuery.data) return 0;
    if ("segments" in gridQuery.data) {
      return gridQuery.data.segments.reduce((sum, s) => sum + s.cohorts.length, 0);
    }
    return gridQuery.data.cohorts.length;
  }, [gridQuery.data]);

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: "auto" }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5">Cohort Retention</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
            How well each signup cohort sticks around. Rows = the month customers first paid. Columns = months elapsed since.
            Switch to NDR to track expansion — values above 100% mean a cohort is growing in revenue.
          </Typography>
          <Link to={"/saas/metrics" as never} style={{ textDecoration: "none" }}>
            <Typography variant="caption" color="primary.main" sx={{ display: "inline-block", mt: 0.5 }}>
              ← Back to SaaS Metrics
            </Typography>
          </Link>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            disabled={backfill.isPending}
            onClick={() => backfill.mutate()}
          >
            {backfill.isPending ? "Backfilling…" : "Backfill history"}
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            disabled={snapshotNow.isPending}
            onClick={() => snapshotNow.mutate()}
          >
            {snapshotNow.isPending ? "Snapshotting…" : "Snapshot now"}
          </Button>
        </Stack>
      </Stack>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>View</InputLabel>
              <Select
                label="View"
                value={view}
                onChange={(e) => setView(e.target.value as "logo" | "ndr")}
              >
                <MenuItem value="logo">Logo retention (count)</MenuItem>
                <MenuItem value="ndr">NDR (MRR)</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Segment</InputLabel>
              <Select
                label="Segment"
                value={segment}
                onChange={(e) => setSegment(e.target.value)}
              >
                {SEGMENT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              type="number"
              label="Min cohort size"
              value={minCohortSize}
              onChange={(e) => setMinCohortSize(Math.max(1, parseInt(e.target.value || "1", 10)))}
              helperText="Cells with n0 below this get dimmed (low-confidence)."
              sx={{ width: 180 }}
              inputProps={{ min: 1 }}
            />

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={`${cohortCount} cohort${cohortCount === 1 ? "" : "s"}`} />
              <ColorLegend />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Grid(s) */}
      {gridQuery.isLoading ? (
        <Card><CardContent sx={{ textAlign: "center", py: 6 }}><CircularProgress /></CardContent></Card>
      ) : gridQuery.error ? (
        <Alert severity="error">{errorMessage(gridQuery.error)}</Alert>
      ) : !gridQuery.data || cohortCount === 0 ? (
        <Card>
          <CardContent sx={{ py: 5, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No cohort data yet.
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 2 }}>
              Snapshots are needed before the grid can render. Backfill pulls every month from your earliest subscription forward.
            </Typography>
            <Button variant="contained" onClick={() => backfill.mutate()} disabled={backfill.isPending}>
              {backfill.isPending ? "Backfilling…" : "Backfill history"}
            </Button>
          </CardContent>
        </Card>
      ) : isSegmented ? (
        <Stack spacing={3}>
          {(gridQuery.data as CohortGridSegmented).segments.map((s) => (
            <Card key={s.segment_value}>
              <CardHeader
                title={
                  <Typography variant="subtitle1">
                    <strong>{s.segment_value}</strong>
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {s.cohorts.length} cohort{s.cohorts.length === 1 ? "" : "s"}
                    </Typography>
                  </Typography>
                }
              />
              <CardContent sx={{ pt: 0 }}>
                <HeatGrid cohorts={s.cohorts} view={view} />
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Card>
          <CardContent>
            <HeatGrid cohorts={(gridQuery.data as CohortGridBlended).cohorts} view={view} />
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? <Alert severity={toast.severity}>{toast.msg}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
}

// ─── Heat-grid ────────────────────────────────────────────────────────────

function HeatGrid({ cohorts, view }: { cohorts: Cohort[]; view: "logo" | "ndr" }) {
  // Find max month_index across all cohorts so every row has the same width.
  const maxMonthIndex = useMemo(() => {
    let m = 0;
    for (const c of cohorts) {
      for (const cell of c.cells) {
        if (cell.month_index > m) m = cell.month_index;
      }
    }
    return m;
  }, [cohorts]);

  const monthColumns = useMemo(
    () => Array.from({ length: maxMonthIndex + 1 }, (_, i) => i),
    [maxMonthIndex],
  );

  // Index each cohort's cells by month_index for O(1) lookup during render.
  const cohortCellMap = useMemo(() => {
    return cohorts.map((c) => {
      const byMonth = new Map<number, CohortCellLogo | CohortCellNdr>();
      for (const cell of c.cells) byMonth.set(cell.month_index, cell);
      return { cohort: c, byMonth };
    });
  }, [cohorts]);

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box
        component="table"
        sx={{
          borderCollapse: "separate",
          borderSpacing: 0,
          width: "max-content",
          "& th, & td": {
            p: 0.5,
            textAlign: "center",
            fontSize: "0.72rem",
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
          },
        }}
      >
        <Box component="thead">
          <Box component="tr">
            <Box component="th" sx={{ minWidth: 90, textAlign: "left !important", color: "text.secondary" }}>
              Cohort
            </Box>
            <Box component="th" sx={{ minWidth: 60, color: "text.secondary" }}>
              {view === "logo" ? "n0" : "MRR0"}
            </Box>
            {monthColumns.map((mi) => (
              <Box component="th" key={mi} sx={{ minWidth: 52, color: "text.secondary" }}>M{mi}</Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {cohortCellMap.map(({ cohort, byMonth }) => (
            <Box component="tr" key={cohort.cohort_month}>
              <Box component="td" sx={{ textAlign: "left !important", fontWeight: 600, color: "text.primary" }}>
                {formatCohortMonth(cohort.cohort_month)}
              </Box>
              <Box component="td" sx={{ color: "text.secondary" }}>
                {view === "logo"
                  ? cohort.n0 ?? "—"
                  : cohort.n0_mrr_cents != null ? dollars(cohort.n0_mrr_cents) : "—"}
              </Box>
              {monthColumns.map((mi) => {
                const cell = byMonth.get(mi);
                return <Cell key={mi} cell={cell} view={view} />;
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function Cell({ cell, view }: { cell: CohortCellLogo | CohortCellNdr | undefined; view: "logo" | "ndr" }) {
  if (!cell) {
    // Empty — cohort hasn't aged into this month yet.
    return <Box component="td" sx={{ bgcolor: "transparent" }} />;
  }
  const lowConf = "low_confidence" in cell && cell.low_confidence;
  const { bg, fg } = colorForValue(cell.value);
  const tooltip =
    view === "logo" && "active" in cell
      ? `${cell.active}/${cell.n0} active · ${cell.value.toFixed(1)}%${lowConf ? " (low confidence)" : ""}`
      : view === "ndr" && "mrr_cents" in cell
        ? `${dollars(cell.mrr_cents)} · ${cell.value.toFixed(1)}%`
        : `${cell.value.toFixed(1)}%`;

  return (
    <Box component="td" sx={{ p: "1px !important" }}>
      <Tooltip title={tooltip} arrow>
        <Box
          sx={{
            bgcolor: bg,
            color: fg,
            opacity: lowConf ? 0.5 : 1,
            border: lowConf ? "1px dotted" : "1px solid",
            borderColor: lowConf ? "warning.main" : "transparent",
            borderRadius: 0.5,
            fontWeight: 600,
            py: 0.5,
            px: 0.5,
            minWidth: 50,
          }}
        >
          {cell.value.toFixed(0)}
        </Box>
      </Tooltip>
    </Box>
  );
}

// ─── Color scale ──────────────────────────────────────────────────────────
//
// Single source of truth — used by Cell + ColorLegend so the legend always
// matches what's actually rendered.
const COLOR_STOPS: { min: number; label: string; bg: string; fg: string }[] = [
  { min: 100, label: "≥100% (expansion)", bg: "#0f766e", fg: "#fff" },
  { min:  90, label: "90–100%",           bg: "#16a34a", fg: "#fff" },
  { min:  75, label: "75–90%",            bg: "#86efac", fg: "#064e3b" },
  { min:  50, label: "50–75%",            bg: "#fef08a", fg: "#713f12" },
  { min:  25, label: "25–50%",            bg: "#fb923c", fg: "#7c2d12" },
  { min:   0, label: "<25%",              bg: "#dc2626", fg: "#fff" },
];

function colorForValue(v: number): { bg: string; fg: string } {
  for (const stop of COLOR_STOPS) {
    if (v >= stop.min) return { bg: stop.bg, fg: stop.fg };
  }
  return { bg: "#e5e7eb", fg: "#374151" };
}

function ColorLegend() {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {[...COLOR_STOPS].reverse().map((stop) => (
        <Tooltip key={stop.label} title={stop.label} arrow>
          <Box
            sx={{
              width: 18,
              height: 14,
              bgcolor: stop.bg,
              borderRadius: 0.5,
            }}
          />
        </Tooltip>
      ))}
    </Stack>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function formatCohortMonth(iso: string): string {
  // Cohort month is a calendar month-end (e.g. "2024-01-31"). Display as
  // "Jan 2024" — that's how analysts speak about cohorts.
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

function dollars(cents: number): string {
  if (cents >= 100_000_00) return `$${(cents / 100_000_000).toFixed(1)}M`;
  if (cents >= 100_000) return `$${(cents / 100_000).toFixed(1)}k`;
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}
